import { createSlice, createAction } from "@reduxjs/toolkit";
import {
  fetchActiveCarePlanData,
  activeCarePlanDetailView,
  fetchDraftCarePlanData,
  fetchSectionsData,
  fetchItemsData,
  checkOrCreateCarePlan,
  saveCarePlanData,
  fetchCarePlanInstance,
  fetchDefaultContacts,
  createDefaultContact,
  updateDefaultContact,
  deleteDefaultContact,
  deleteCarePlan,
  fetchOverviewDraftCarePlans,
  updateSectionAction,
  lockCarePlan,
  addUsersToCarePlan,
  removeUserFromCarePlan,
  loadFeedbacks,
  getDataForView,
  fetchCarePlanAuditLogs,
} from "../actions/careplanActions";
import {} from "../actions/careplanActions";

const careplanSlice = createSlice({
  name: "careplan",
  initialState: {
    overviewDraftCarePlans: [],
    overviewDraftLoading: false,
    // Active care plan states
    activeCarePlans: {},
    activeTotalRows: 0,
    activeCurrentPage: 1,
    activeRowsPerPage: 12,
    activeSearchQuery: { nhsNumber: "", createdBy: "" },
    activeLoading: false,
    // Draft care plan states
    draftCarePlans: {},
    draftTotalRows: 0,
    draftCurrentPage: 1,
    draftRowsPerPage: 12,
    draftSearchQuery: { nhsNumber: "", createdBy: "" },
    draftLoading: false,

    selectedActiveCarePlan: null,
    sections: [],
    items: [],

    defaultContactsLoaded: false,

    draftError: null,
    sectionsLoaded: false,
    itemsLoaded: false,
    loading: false,
    error: null,
    // Add these for care plan creation
    carePlan: {
      id: null,
      public_id: null,
      nhsNumber: "",
      sections: [], // Will hold sections with items and answers
      contacts: [], // Include contacts in the initial state
      important_info: "",
      color: "",
    },
    currentSectionIndex: 0,

    feedbackList: [],
    feedbackLoading: false,
    feedbackError: null,

    carePlanLogs: {}, // { [carePlanId]: [ log, … ] }
    carePlanLogsLoading: {}, // { [carePlanId]: boolean }

    mode: "view", // 'create' | 'edit' | 'view'
  },
  reducers: {
    updateNhsNumber(state, action) {
      state.carePlan.nhsNumber = action.payload;
    },
    loadCarePlan(state, action) {
      state.carePlan = action.payload;
    },
    updateAnswer(state, action) {
      const { sectionId, itemId, answer } = action.payload;
      const section = state.carePlan.sections.find((s) => s.id === sectionId);
      if (section) {
        const item = section.items.find((i) => i.id === itemId);
        if (item) {
          item.answer = answer;
        }
      }
    },
    updateContacts: (state, action) => {
      state.carePlan.contacts = action.payload; // Update contacts in care plan
    },
    updateImportantInfo: (state, action) => {
      state.carePlan.important_info = action.payload; // Update contacts in care plan
    },
    updateSectionItems: (state, action) => {
      const { sectionId, items } = action.payload;
      const sectionIndex = state.carePlan.sections.findIndex(
        (section) => section.id === sectionId
      );
      if (sectionIndex !== -1) {
        state.carePlan.sections[sectionIndex].items = items;
      }
    },
    setCurrentSectionIndex(state, action) {
      state.currentSectionIndex = action.payload;
    },
    setMode(state, { payload }) {
      state.mode = payload; // 'create' | 'edit' | 'view'
    },
    loadCarePlanSections(state, action) {
      if (state.mode !== "create") return; // ← DO NOT touch in edit
      state.carePlan.sections = action.payload.map((section) => ({
        ...section,
        items: (section.items || []).map((i) => ({ id: i.id, answer: "" })),
      }));
    },
    loadCarePlanItems(state, action) {
      if (state.mode !== "create") return; // ← DO NOT touch in edit
      const allItems = action.payload;
      state.carePlan.sections = (state.carePlan.sections || []).map(
        (section) => ({
          ...section,
          items: (section.items || []).map((ref) => {
            const full = allItems.find((i) => i.id === ref.id);
            // If not found yet, keep a minimal shell so the UI and sidebar still render
            if (!full) {
              return {
                id: ref.id,
                title: "",
                description: "",
                image_url: null,
                prompt_text: "",
                standard_answers: [],
                section_overrides: {},
                section_prompt_overrides: {},
                user_defined: false,
                answer: ref.answer,
              };
            }
            return { ...full, answer: ref.answer };
          }),
        })
      );
    },
    setDraftSearchQuery(state, action) {
      state.draftSearchQuery = action.payload;
      state.draftCurrentPage = 1; // reset to first page when filters change
      state.draftCarePlans = {}; // clear cached pages, if needed
    },
    setActiveSearchQuery(state, action) {
      state.activeSearchQuery = action.payload;
      state.activeCurrentPage = 1; // reset to first page when filters change
      state.activeCarePlans = {}; // clear cached pages, if needed
    },
    loadCarePlanInstance(state, action) {
      state.carePlan = action.payload;
    },
    updateDraftCarePlan(state, action) {
      const updatedCarePlan = action.payload;

      // Use mdtUsers as the source of truth if assigned_users is not provided
      const newAssignedUsers =
        updatedCarePlan.assigned_users || updatedCarePlan.mdtUsers;

      for (const page in state.draftCarePlans) {
        const pageData = state.draftCarePlans[page];
        const index = pageData.findIndex((cp) => cp.id === updatedCarePlan.id);
        if (index !== -1) {
          // Replace the care plan at this index with the updated one
          pageData[index] = {
            ...pageData[index],
            mdtUsers: updatedCarePlan.mdtUsers,
            assigned_users: newAssignedUsers,
            updated_at: updatedCarePlan.updated_at, // Update the updated_at field
          };
          state.draftCarePlans[page] = [...pageData]; // Ensure immutability
          break;
        }
      }
    },
    setActiveCurrentPage(state, action) {
      state.activeCurrentPage = action.payload;
    },
    setDraftCurrentPage(state, action) {
      state.draftCurrentPage = action.payload;
    },
    // This action invalidates (clears) all cached pages after a certain page
    invalidatePagesAfter: (state, action) => {
      const { type, fromPage } = action.payload;
      if (type === "draft") {
        Object.keys(state.draftCarePlans)
          .map(Number)
          .forEach((pageNum) => {
            if (pageNum > fromPage) {
              delete state.draftCarePlans[pageNum];
            }
          });
      } else if (type === "active") {
        Object.keys(state.activeCarePlans)
          .map(Number)
          .forEach((pageNum) => {
            if (pageNum > fromPage) {
              delete state.activeCarePlans[pageNum];
            }
          });
      }
    },
    updateCarePlanLockStatus: (state, action) => {
      const { id, is_locked, locked_by } = action.payload;

      // Update the care plan in all pages
      for (const page in state.draftCarePlans) {
        state.draftCarePlans[page] = state.draftCarePlans[page].map((cp) => {
          if (cp.id === id) {
            return {
              ...cp,
              is_locked,
              locked_by,
            };
          }
          return cp;
        });
      }

      // ALSO update the one plan inside overviewDraftCarePlans
      state.overviewDraftCarePlans = state.overviewDraftCarePlans.map((cp) =>
        cp.id === id ? { ...cp, is_locked, locked_by } : cp
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverviewDraftCarePlans.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.overviewDraftCarePlans = data;
        state.overviewDraftLoading = false;
      })

      .addCase(fetchOverviewDraftCarePlans.pending, (state) => {
        state.overviewDraftLoading = true;
      })
      // Active Care Plan Cases
      .addCase(fetchActiveCarePlanData.fulfilled, (state, action) => {
        const { data, totalRows, page, rowsPerPage, searchQuery } =
          action.payload;

        const rowsOrSearchChanged =
          state.activeRowsPerPage !== rowsPerPage ||
          state.activeSearchQuery.nhsNumber !==
            (searchQuery?.nhsNumber || "") ||
          state.activeSearchQuery.createdBy !== (searchQuery?.createdBy || "");

        if (rowsOrSearchChanged) {
          state.activeCarePlans = {};
          state.activeTotalRows = 0;
          state.activeCurrentPage = 1;
        }

        state.activeRowsPerPage = rowsPerPage;
        state.activeSearchQuery = searchQuery || {
          nhsNumber: "",
          createdBy: "",
        };
        state.activeCurrentPage = page;
        state.activeCarePlans[page] = data;
        state.activeTotalRows = totalRows;
        state.activeLoading = false;
      })

      .addCase(activeCarePlanDetailView.fulfilled, (state, action) => {
        const updatedCarePlan = action.payload;
        // If the updated care plan is in the current pagination, update it
        for (const page in state.activeCarePlans) {
          const index = state.activeCarePlans[page].findIndex(
            (cp) => cp.id === updatedCarePlan.id
          );
          if (index !== -1) {
            state.activeCarePlans[page][index] = updatedCarePlan;
            break;
          }
        }
        // If selectedCarePlan is the one incremented, update it
        if (
          state.selectedCarePlan &&
          state.selectedCarePlan.id === updatedCarePlan.id
        ) {
          state.selectedCarePlan = updatedCarePlan;
        }
      })

      // Sections Data Cases
      .addCase(fetchSectionsData.fulfilled, (state, action) => {
        state.sections = action.payload.map((section) => {
          if (section.isServiceUserSpecific) {
            return {
              ...section,
              items: [], // Initialize with an empty items array is user specific section
            };
          } else {
            return section;
          }
        });
        state.sectionsLoaded = true;
      })

      // Items Data Cases
      .addCase(fetchItemsData.fulfilled, (state, action) => {
        state.items = action.payload;
        state.itemsLoaded = true;
      })

      .addCase(checkOrCreateCarePlan.fulfilled, (state, action) => {
        // Only update state.carePlan if a new care plan was created
        if (!action.payload.exists) {
          state.carePlan = action.payload.care_plan;
        }
      })

      .addCase(fetchDefaultContacts.fulfilled, (state, action) => {
        // set default contacts as initial contacts
        state.carePlan.contacts = action.payload;
        state.defaultContactsLoaded = true;
      })
      .addCase(createDefaultContact.fulfilled, (state, action) => {
        state.carePlan.contacts.push(action.payload);
      })
      .addCase(updateDefaultContact.fulfilled, (s, { payload }) => {
        const i = s.carePlan.contacts.findIndex((c) => c.id === payload.id);
        if (i >= 0) s.carePlan.contacts[i] = payload;
      })
      .addCase(deleteDefaultContact.fulfilled, (s, { payload: id }) => {
        s.carePlan.contacts = s.carePlan.contacts.filter((c) => c.id !== id);
      })

      .addCase(saveCarePlanData.fulfilled, (state, action) => {
        // const updatedFields = action.payload;
        // state.carePlan = {
        //   ...state.carePlan,
        //   ...updatedFields,
        // };
        state.carePlan = action.payload;
      })

      .addCase(fetchDraftCarePlanData.fulfilled, (state, action) => {
        const { data, totalRows, page, rowsPerPage, searchQuery } =
          action.payload;

        // If rowsPerPage or searchQuery changed, reset and then set:
        const rowsOrSearchChanged =
          state.draftRowsPerPage !== rowsPerPage ||
          state.draftSearchQuery.nhsNumber !== (searchQuery?.nhsNumber || "") ||
          state.draftSearchQuery.createdBy !== (searchQuery?.createdBy || "");

        if (rowsOrSearchChanged) {
          state.draftCarePlans = {};
          state.draftTotalRows = 0;
          state.draftCurrentPage = 1;
        }

        state.draftRowsPerPage = rowsPerPage;
        state.draftSearchQuery = searchQuery || {
          nhsNumber: "",
          createdBy: "",
        };
        state.draftCurrentPage = page;
        state.draftCarePlans[page] = data;
        state.draftTotalRows = totalRows;
        state.draftLoading = false;
      })

      .addCase(fetchCarePlanInstance.fulfilled, (state, action) => {
        // state.carePlanInstance = action.payload;
        state.carePlan = action.payload;
      })

      // add new user defining items to the careplan while creating care plan
      .addCase(addNewItem, (state, action) => {
        const { sectionId, item } = action.payload;
        const section = state.carePlan.sections.find(
          (section) => section.id === sectionId
        );
        if (section) {
          if (!section.items) {
            section.items = [];
          }
          section.items.push(item);
        }
      })

      // remove user defined items from the careplan while creating care plan
      .addCase(removeItem, (state, action) => {
        const { sectionId, itemId } = action.payload;
        const section = state.carePlan.sections.find(
          (section) => section.id === sectionId
        );
        if (section) {
          section.items = section.items.filter((item) => item.id !== itemId);
        }
      })

      // clear carePlan when starting a new creation flow
      .addCase(clearCarePlan, (state) => {
        state.carePlan = {
          id: null,
          nhsNumber: "",
          sections: [],
          contacts: [],
          important_info: "",
          color: "",
        };
      })

      // delete draft care plan
      .addCase(deleteCarePlan.fulfilled, (state, action) => {
        const { carePlanId, type } = action.payload;

        if (type === "draft") {
          const { draftCurrentPage, draftCarePlans } = state;
          const currentPageData = draftCarePlans[draftCurrentPage] || [];
          const newPageData = currentPageData.filter(
            (plan) => plan.id !== carePlanId
          );

          state.draftCarePlans[draftCurrentPage] = newPageData;

          // If the current page has fewer items and there are items in the next page, consider refetching
          // Alternatively, handle page decrement if the current page becomes empty
          if (newPageData.length === 0 && draftCurrentPage > 1) {
            state.draftCurrentPage -= 1;
          }
        } else if (type === "active") {
          const { activeCurrentPage, activeCarePlans } = state;
          const currentPageData = activeCarePlans[activeCurrentPage] || [];
          const newPageData = currentPageData.filter(
            (plan) => plan.id !== carePlanId
          );

          state.activeCarePlans[activeCurrentPage] = newPageData;

          if (newPageData.length === 0 && activeCurrentPage > 1) {
            state.activeCurrentPage -= 1;
          }
        }
      })

      .addCase(updateSectionAction.fulfilled, (state, action) => {
        const updatedSection = action.payload;

        // 1. Find the care plan that contains the section to update (if applicable)
        if (state.carePlan && state.carePlan.sections) {
          const sectionIndex = state.carePlan.sections.findIndex(
            (sec) => Number(sec.id) === Number(updatedSection.id)
          );

          if (sectionIndex !== -1) {
            // 2. Immutably update the sections array within the care plan
            state.carePlan.sections = [
              ...state.carePlan.sections.slice(0, sectionIndex),
              { ...state.carePlan.sections[sectionIndex], ...updatedSection },
              ...state.carePlan.sections.slice(sectionIndex + 1),
            ];
          }
        }

        // (Optional) Update the general sections list if needed:
        const generalIndex = state.sections.findIndex(
          (sec) => Number(sec.id) === Number(updatedSection.id)
        );
        if (generalIndex !== -1) {
          state.sections[generalIndex] = {
            ...state.sections[generalIndex],
            ...updatedSection,
          };
        }
      })

      .addCase(lockCarePlan.fulfilled, (state, action) => {
        const { id, locked_by } = action.payload;

        // Update the care plan in all pages
        for (const page in state.draftCarePlans) {
          state.draftCarePlans[page] = state.draftCarePlans[page].map((cp) => {
            if (cp.id === id) {
              return {
                ...cp,
                is_locked: true,
                locked_by,
              };
            }
            return cp;
          });
        }
      })

      .addCase(lockCarePlan.rejected, (state, action) => {
        if (action.payload?.code === "ALREADY_LOCKED") {
          const { care_plan_id, locked_by } = action.payload;

          // Update the care plan in all pages with the lock info
          for (const page in state.draftCarePlans) {
            state.draftCarePlans[page] = state.draftCarePlans[page].map(
              (cp) => {
                if (cp.id === care_plan_id) {
                  return {
                    ...cp,
                    is_locked: true,
                    locked_by,
                  };
                }
                return cp;
              }
            );
          }
        }
      })

      // whenever you add users, update the matching carePlan in draftCarePlans
      .addCase(addUsersToCarePlan.fulfilled, (state, action) => {
        const updated = action.payload; // full carePlan with .assigned_users
        Object.values(state.draftCarePlans).forEach((pageArray) => {
          const idx = pageArray.findIndex((cp) => cp.id === updated.id);
          if (idx !== -1) {
            pageArray[idx].assigned_users = updated.mdtUsers;
          }
        });
      })

      // and the same for removal
      .addCase(removeUserFromCarePlan.fulfilled, (state, action) => {
        const updated = action.payload;
        Object.values(state.draftCarePlans).forEach((pageArray) => {
          const idx = pageArray.findIndex((cp) => cp.id === updated.id);
          if (idx !== -1) {
            pageArray[idx].assigned_users = updated.mdtUsers;
          }
        });
      })

      .addCase(loadFeedbacks.fulfilled, (state, { payload }) => {
        state.feedbackLoading = false;
        state.feedbackList = payload;
      })

      .addCase(getDataForView.pending, (state) => {
        state.loadingInstance = true;
        state.errorInstance = null;
      })
      .addCase(getDataForView.fulfilled, (state, { payload }) => {
        state.loadingInstance = false;
        state.carePlan = payload;
      })
      .addCase(getDataForView.rejected, (state, { payload }) => {
        state.loadingInstance = false;
        state.errorInstance = payload;
      })

      .addCase(fetchCarePlanAuditLogs.pending, (state, action) => {
        state.carePlanLogsLoading[action.meta.arg.carePlanId] = true;
      })
      .addCase(fetchCarePlanAuditLogs.fulfilled, (state, { payload }) => {
        const { carePlanId, logs } = payload;
        state.carePlanLogsLoading[carePlanId] = false;
        state.carePlanLogs[carePlanId] = logs;
      });
  },
});

export const {
  loadCarePlan,
  setMode,
  loadCarePlanSections,
  loadCarePlanItems,
  loadCarePlanInstance,
  updateNhsNumber,
  updateAnswer,
  updateContacts,
  updateImportantInfo,
  updateSectionItems,
  setCurrentSectionIndex,
  updateDraftCarePlan,
  invalidatePagesAfter,
  setActiveCurrentPage,
  setDraftCurrentPage,
  setDraftSearchQuery,
  setActiveSearchQuery,
} = careplanSlice.actions;

export const addNewItem = createAction("careplan/addNewItem");
export const removeItem = createAction("careplan/removeItem");
export const clearCarePlan = createAction("careplan/clearCarePlan"); //action to clear carePlan when starting a new creation flow to avoid residual data

export default careplanSlice.reducer;
